import { INK_MID } from './chartColors.js'

interface HeatGridProps {
  rows: readonly string[]
  columns: readonly string[]
  /** `values[row][column]`; null for a cell with nothing. */
  values: readonly (readonly (number | null)[])[]
  /** The fill for a value, light to dark; a value of null is the surface. */
  ramp: readonly string[]
  format: (value: number) => string
  title: string
}

/** A grid of cells shaded on one ramp by their value, the value written in each so the shade is never the only reading. */
export function HeatGrid({ rows, columns, values, ramp, format, title }: HeatGridProps) {
  const present = values.flat().filter((value): value is number => value !== null)
  const max = Math.max(1, ...present)
  const shade = (value: number | null): string => {
    if (value === null) {
      return '#F7FBFC'
    }
    const step = Math.min(ramp.length - 1, Math.floor((value / max) * ramp.length))
    return ramp[step] ?? ramp[ramp.length - 1] ?? '#F7FBFC'
  }
  const dark = (value: number | null): boolean => value !== null && value / max > 0.6
  return (
    <table role="img" aria-label={title} className="w-full border-separate text-[11px]" style={{ borderSpacing: 3 }}>
      <thead>
        <tr>
          <th />
          {columns.map((column) => (
            <th key={column} className="pb-1 text-center font-bold" style={{ color: INK_MID }}>
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={row}>
            <th className="pr-2 text-right font-bold" style={{ color: INK_MID }}>
              {row}
            </th>
            {columns.map((column, columnIndex) => {
              const value = values[rowIndex]?.[columnIndex] ?? null
              return (
                <td key={column} className="h-11 rounded text-center font-bold" style={{ background: shade(value), color: dark(value) ? '#fff' : '#0A2830' }} title={`${row} ${column}: ${value === null ? 'nothing' : format(value)}`}>
                  {value === null ? '—' : format(value)}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
